import 'date-fns';
import React, {useEffect} from 'react';
import {
    Box,
    Button,
    Grid,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Tooltip,
    FormHelperText,
    TextField,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import {z} from 'zod';
import {fieldOptions} from '@/Core/mobx/ParameterStore/ApprovalXref';
import {Controller, FormProvider, useFieldArray, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {NumberFormatCustom} from '@/Core/UI';
import {useCommonModalStyles} from '@/Agent/Quotes/InstantAppModalPages/common/modalStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import csx from 'classnames';
import Divider from '@material-ui/core/Divider';
import FixedNavigation from '@/Agent/Quotes/InstantAppModalPages/common/FixedNavigation';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import Utils from '@/Core/Utils';
import {Autocomplete} from '@material-ui/lab';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {Approval} from '@/Core/mobx/ParameterStore/Approval';

const notListedProvider = 'My provider is not listed';

const typeOptions = [
    'Annuities',
    'Accidental Death',
    'Guarantee Issue',
    'Guarantee Universal Life',
    'Indexed Universal Life',
    'Variable Universal Life',
    'Term',
    'Whole Life',
];

const typeOptionsVarName = typeOptions.map((v) => Utils.MakeVarName(v));

interface ExistingLifeInsuranceProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
}

const useStyles = makeStyles(() => ({
    container: {
        maxWidth: 700,
        width: '100%',
        position: 'relative',
        paddingBottom: 113,
        margin: '0 auto',
    },
    title: {
        marginBottom: 16,
        marginTop: 40,
        fontFamily: 'LoraSemiBold',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 40,
        lineHeight: '110%',
        color: '#456d69',
        opacity: 0.9,
    },
    subtitle: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '27px',
        color: '#666666',
        marginBottom: 32,
    },
    buttonWrapper: {
        display: 'flex',
        columnGap: '24px',
    },
    btn: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '28px',
        lineHeight: '140%',
        borderWidth: '1.75px',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: 337,
        width: '100%',
        color: '#174943',
        textTransform: 'none',
        borderColor: '#174943',
        '&:hover': {
            borderWidth: '1.75px',
            borderColor: '#174943',
            backgroundColor: 'rgba(90, 226, 136, 0.04)',
        },
    },
    activeBtn: {
        backgroundColor: '#EDFCF2',
    },
    form: {
        display: 'flex',
        rowGap: 40,
    },
    addBtn: {
        color: '#74928E',
        fontFamily: 'FredokaRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 18,
        lineHeight: '160%',
        textTransform: 'none',
    },
    removeBtn: {
        marginTop: 16,
        alignSelf: 'flex-end',
    },
    checkboxLabel: {
        color: '#174943',
        fontFamily: 'FredokaRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 16,
        lineHeight: '140%',
        display: 'flex',
        alignItems: 'center',
    },
    checkboxContainer: {
        '&:hover $checkboxRoot::after': {
            backgroundColor: '#D9D9D9',
        },
        '&:hover .Mui-checked$checkboxRoot::after': {
            backgroundColor: 'transparent',
        },
    },
    checked: {
        width: 20,
        height: 20,
        borderRadius: 4,
        border: '1.5px solid #174943',
        backgroundColor: '#5AE288',
        boxSizing: 'border-box',
        padding: 2,
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        border: '1.5px solid #174943',
        boxSizing: 'border-box',
        padding: 2,
    },
    checkboxRoot: {
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            width: 14,
            height: 14,
            borderRadius: 2,
            backgroundColor: 'transparent',
        },
        '&.MuiIconButton-root:hover': {
            backgroundColor: 'transparent',
        },
    },
    tooltipTitle: {
        color: '#000000',
        fontFamily: 'FredokaMedium',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: 28,
        lineHeight: '120%',
        marginBottom: 8,
    },
    tooltip: {
        zIndex: 100,
        opacity: 1,
        padding: '26px 32px',
        minWidth: '230px',
        maxWidth: '388px',
        backgroundColor: '#ffffff',
        border: '2px solid #5ae288',
        boxShadow: '0 0 8px rgba(0, 0, 0, 0.25)',
        borderRadius: '20px',
        outline: 'none',
    },
    tooltipText: {
        color: '#174943',
        fontFamily: 'FredokaRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: 18,
        lineHeight: '120%',
    },
    infoBtn: {
        marginLeft: 8,
        width: 18,
        height: 18,
        cursor: 'help',
        fill: '#74928E',
    },
}));

const policyFields = z
    .object({
        provider: z.string().min(1, 'Coverage Provider is required'),
        type: z
            .string()
            .min(1, 'Coverage Provider is required')
            .refine((val) => val.toLowerCase() !== 'unknown', {
                message: 'Coverage Provider is required',
            }),
        amount: z.number().min(1, 'Coverage Amount is required'),
        unlistedProvider: z.string().optional(),
        confirmNoExchangeOrReplacement: z.boolean().refine((value) => value),
    })
    .superRefine((data, ctx) => {
        if (data.provider === notListedProvider && !data.unlistedProvider) {
            ctx.addIssue({
                path: ['unlistedProvider'],
                message: 'Coverage Provider is required',
                code: z.ZodIssueCode.custom,
            });
        }
    });

const formSchema = z
    .object({
        existingCoverage: z.boolean().refine((value) => value || !value),
        policies: z.array(policyFields),
    })
    .superRefine((data, ctx) => {
        if (data.existingCoverage && data.policies.length === 0) {
            ctx.addIssue({
                path: ['policies'],
                message: 'At least one policy is required if existing coverage is selected.',
                code: z.ZodIssueCode.custom,
            });
        }
    });

const ExistingLifeInsurance: React.FC<ExistingLifeInsuranceProps> = observer(
    ({onNext, onBack, onExit, parameterStore, contactFormStore}) => {
        const classes = useStyles();
        const {approval, onValueChangedPreApproval} = parameterStore;

        const initPolicies = (() => {
            const policies = [];
            for (let i = 0; i < 3; i++) {
                const unlistedProvider = approval[`existingCarrier${i === 0 ? '' : i}` as keyof Approval]?.value || '';
                if (approval[`existingCoverageAmount${i === 0 ? '' : i}` as keyof Approval]?.value) {
                    policies.push({
                        type: approval[`existingPolicyType${i === 0 ? '' : i}` as keyof Approval]?.value || '',
                        provider:
                            unlistedProvider.length &&
                            !fieldOptions.firstCarrierQuoted.options.includes(unlistedProvider)
                                ? notListedProvider
                                : approval[`existingCarrier${i === 0 ? '' : i}` as keyof Approval]?.value || '',
                        amount:
                            Number(approval[`existingCoverageAmount${i === 0 ? '' : i}` as keyof Approval]?.value) ||
                            '',
                        confirmNoExchangeOrReplacement:
                            approval[`replacingExistingPolicy${i === 0 ? '' : i}` as keyof Approval]?.value === false,
                        unlistedProvider:
                            unlistedProvider.length &&
                            !fieldOptions.firstCarrierQuoted.options.includes(unlistedProvider)
                                ? unlistedProvider
                                : '',
                    });
                }
            }
            return policies.length
                ? policies
                : [
                      {
                          type: '',
                          provider: '',
                          amount: '',
                          confirmNoExchangeOrReplacement: false,
                          unlistedProvider: '',
                      },
                  ];
        })();

        const methods = useForm({
            resolver: zodResolver(formSchema),
            mode: 'onChange',
            defaultValues: {
                existingCoverage: approval.existingCoverage.value || '',
                policies: initPolicies,
            },
        });

        console.log('methods', methods);

        const {
            control,
            watch,
            setValue,
            handleSubmit,
            formState: {isValid, isSubmitting},
        } = methods;
        const commonClasses = useCommonModalStyles();
        const {fields, append, remove} = useFieldArray({
            control,
            name: 'policies',
        });

        const onSubmit = (data: any) => {
            onValueChangedPreApproval({...approval.existingCoverage, name: 'existingCoverage'}, data.existingCoverage);
            for (let i = 0; i < 3; i++) {
                onValueChangedPreApproval(
                    approval[`existingPolicyType${i || ''}` as keyof Approval],
                    i < data.policies.length ? data.policies[i].type : '',
                );
                onValueChangedPreApproval(
                    approval[`existingCarrier${i || ''}` as keyof Approval],
                    i < data.policies.length ? data.policies[i].unlistedProvider || data.policies[i].provider : '',
                );
                onValueChangedPreApproval(
                    approval[`existingCoverageAmount${i || ''}` as keyof Approval],
                    i < data.policies.length ? data.policies[i].amount : '',
                );
                onValueChangedPreApproval(
                    approval[`replacingExistingPolicy${i || ''}` as keyof Approval],
                    i < data.policies.length ? !data.policies[i].confirmNoExchangeOrReplacement : false,
                );
            }
            contactFormStore.saveForm();
            onNext();
        };

        const watchExistingCoverage = watch('existingCoverage');
        const watchPolicies = watch('policies');

        console.log('isValid', isValid);
        console.log('fields', fields);

        useEffect(() => {
            if (watchExistingCoverage === true && watchPolicies.length === 0) {
                setValue('policies', initPolicies, {shouldValidate: true});
            }
            if (watchExistingCoverage === false && watchPolicies.length !== 0) {
                setValue('policies', [], {shouldValidate: true});
            }
        }, [watchExistingCoverage]);

        return (
            <FormProvider {...methods}>
                <Box className={classes.container}>
                    <Typography className={classes.title}>
                        Do you have any existing life insurance or annuity contracts with this or any other company?
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Life insurance companies will be able to see all of your existing policies with them and other
                        companies, so being honest here will help you get a decision faster!
                    </Typography>
                    <Grid container component="form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                        <Grid item xs={12} classes={{root: classes.buttonWrapper}}>
                            <Controller
                                name="existingCoverage"
                                control={control}
                                render={({field}) => (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            classes={{
                                                root: csx(classes.btn, {
                                                    [classes.activeBtn]: field.value === true,
                                                }),
                                            }}
                                            onClick={() => field.onChange(true)}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            classes={{
                                                root: csx(classes.btn, {
                                                    [classes.activeBtn]: field.value === false,
                                                }),
                                            }}
                                            onClick={() => field.onChange(false)}
                                        >
                                            No
                                        </Button>
                                    </>
                                )}
                            />
                        </Grid>
                        {watchExistingCoverage && (
                            <>
                                {fields.map((item, index) => {
                                    const watchProvider = watch(`policies.${index}.provider`);
                                    return (
                                        <Box
                                            key={item.id}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                ...(index === fields.length - 1 && {paddingBottom: '40px'}),
                                            }}
                                        >
                                            <Grid
                                                container
                                                spacing={3}
                                                justify="center"
                                                style={{alignItems: 'flex-start'}}
                                            >
                                                <Grid item xs={12}>
                                                    <Typography className={commonClasses.label}>
                                                        Coverage Provider{fields.length > 1 && ` #${index + 1}`}
                                                    </Typography>
                                                    <Controller
                                                        name={`policies.${index}.provider`}
                                                        control={control}
                                                        render={({field, fieldState: {error}}) => (
                                                            <Autocomplete
                                                                {...field}
                                                                clearOnBlur
                                                                options={[
                                                                    ...fieldOptions.firstCarrierQuoted.options,
                                                                    notListedProvider,
                                                                ]}
                                                                value={field.value || ''}
                                                                onChange={(_, newValue) => field.onChange(newValue)}
                                                                filterOptions={(options, state) => {
                                                                    const results = options.filter((v) =>
                                                                        v
                                                                            .toLowerCase()
                                                                            .includes(state.inputValue.toLowerCase()),
                                                                    );
                                                                    return results.length
                                                                        ? results
                                                                        : [notListedProvider];
                                                                }}
                                                                className={commonClasses.autocomplete}
                                                                popupIcon={<ExpandMoreIcon />}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        fullWidth
                                                                        aria-label="Coverage Provider"
                                                                        variant="outlined"
                                                                        placeholder="Who is your coverage provider?"
                                                                        error={!!error}
                                                                        classes={{root: commonClasses.input}}
                                                                        helperText={error?.message}
                                                                    />
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                {watchProvider === notListedProvider && (
                                                    <Grid item xs={12}>
                                                        <Typography className={commonClasses.label}>
                                                            Name Unlisted Provider
                                                        </Typography>
                                                        <Controller
                                                            name={`policies.${index}.unlistedProvider`}
                                                            control={control}
                                                            render={({field, fieldState: {error}}) => (
                                                                <InputText
                                                                    {...field}
                                                                    aria-label="Name Unlisted Provider"
                                                                    placeholder="Please type the name of your coverage provider"
                                                                    error={!!error}
                                                                    helperText={error?.message}
                                                                    classes={{root: commonClasses.input}}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                )}
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={commonClasses.label}>
                                                        Coverage Amount
                                                    </Typography>
                                                    <Controller
                                                        name={`policies.${index}.amount`}
                                                        control={control}
                                                        render={({field, fieldState: {error}}) => (
                                                            <InputText
                                                                {...field}
                                                                value={String(field.value) || ''}
                                                                aria-label="Coverage Amount"
                                                                placeholder="--"
                                                                error={!!error}
                                                                helperText={error?.message}
                                                                classes={{root: commonClasses.input}}
                                                                onChange={(e) => {
                                                                    field.onChange(Number(e.target.value));
                                                                }}
                                                                InputProps={{
                                                                    inputComponent: NumberFormatCustom,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={commonClasses.label}>
                                                        Coverage Type
                                                    </Typography>
                                                    <Controller
                                                        name={`policies.${index}.type`}
                                                        control={control}
                                                        render={({field, fieldState: {error}}) => (
                                                            <>
                                                                <Select
                                                                    {...field}
                                                                    fullWidth
                                                                    aria-label="Coverage Type"
                                                                    variant="outlined"
                                                                    displayEmpty
                                                                    error={!!error}
                                                                    className={commonClasses.select}
                                                                    IconComponent={ExpandMoreIcon}
                                                                >
                                                                    <MenuItem value="" disabled>
                                                                        Coverage Type
                                                                    </MenuItem>
                                                                    {typeOptions.map((value) => (
                                                                        <MenuItem
                                                                            key={value}
                                                                            value={Utils.MakeVarName(value)}
                                                                        >
                                                                            {value}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                {!!error && (
                                                                    <FormHelperText
                                                                        error={!!error}
                                                                        style={{margin: '3px 14px'}}
                                                                    >
                                                                        {error?.message}
                                                                    </FormHelperText>
                                                                )}
                                                            </>
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} className={commonClasses.input}>
                                                    <Controller
                                                        name={`policies.${index}.confirmNoExchangeOrReplacement`}
                                                        control={control}
                                                        render={({field}) => (
                                                            <FormControlLabel
                                                                classes={{
                                                                    root: classes.checkboxContainer,
                                                                    label: classes.checkboxLabel,
                                                                }}
                                                                control={
                                                                    <Checkbox
                                                                        {...field}
                                                                        disableRipple
                                                                        color="primary"
                                                                        classes={{root: classes.checkboxRoot}}
                                                                        checked={field.value}
                                                                        checkedIcon={
                                                                            <img
                                                                                src="/img/checked.svg"
                                                                                alt="Checked"
                                                                                className={classes.checked}
                                                                            />
                                                                        }
                                                                        icon={<Box className={classes.checkbox} />}
                                                                    />
                                                                }
                                                                label={
                                                                    <>
                                                                        I confirm that this policy is not being used for
                                                                        a 1035 Exchange or Replacement.
                                                                        <Tooltip
                                                                            classes={{tooltip: classes.tooltip}}
                                                                            title={
                                                                                <>
                                                                                    <Typography
                                                                                        classes={{
                                                                                            root: classes.tooltipTitle,
                                                                                        }}
                                                                                    >
                                                                                        1035 or Replacement
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        classes={{
                                                                                            root: classes.tooltipText,
                                                                                        }}
                                                                                    >
                                                                                        1035 exchange allows you to swap
                                                                                        one life insurance policy for
                                                                                        another without creating a
                                                                                        taxable event. A policy
                                                                                        replacement refers to ending or
                                                                                        surrendering an existing life
                                                                                        insurance policy in order to
                                                                                        purchase a new one. By
                                                                                        confirming “No,” you’re
                                                                                        indicating this application is
                                                                                        not replacing an existing policy
                                                                                        and does not involve a 1035
                                                                                        exchange.
                                                                                    </Typography>
                                                                                </>
                                                                            }
                                                                            placement="top"
                                                                        >
                                                                            <InfoOutlinedIcon
                                                                                className={classes.infoBtn}
                                                                            />
                                                                        </Tooltip>
                                                                    </>
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            </Grid>
                                            {fields.length > 1 && (
                                                <Button
                                                    type="button"
                                                    disableElevation
                                                    variant="contained"
                                                    onClick={() => remove(index)}
                                                    className={classes.removeBtn}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                            {index < 2 && <Divider style={{marginTop: '40px'}} />}
                                            {index < 2 && index === fields.length - 1 && (
                                                <>
                                                    <Box
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            gap: 16,
                                                            paddingTop: '10px',
                                                            paddingBottom: '10px',
                                                        }}
                                                    >
                                                        <Button
                                                            className={classes.addBtn}
                                                            type="button"
                                                            onClick={() =>
                                                                append({
                                                                    type: '',
                                                                    provider: '',
                                                                    amount: '',
                                                                    confirmNoExchangeOrReplacement: false,
                                                                    unlistedProvider: '',
                                                                })
                                                            }
                                                        >
                                                            + Add Another Policy
                                                        </Button>
                                                    </Box>
                                                    <Divider />
                                                </>
                                            )}
                                        </Box>
                                    );
                                })}
                            </>
                        )}
                        <FixedNavigation onBack={onBack} isNextDisabled={!isValid} isLoading={isSubmitting} />
                    </Grid>
                </Box>
            </FormProvider>
        );
    },
);

export default ExistingLifeInsurance;
